import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Button, Badge } from 'antd';
import {
  UserOutlined, TeamOutlined, EnvironmentOutlined, StarOutlined,
  LogoutOutlined, SettingOutlined, MenuOutlined, MailOutlined, SearchOutlined,
  FireOutlined, TrophyOutlined, DollarOutlined,
} from '@ant-design/icons';
import NotificationCenter from '../../components/NotificationCenter';
import TeamSwitcher from '../../components/community/TeamSwitcher';
import ContextSwitcher from '../../components/community/ContextSwitcher';
import PushNotificationButton from '../../components/PushNotificationButton';
import ChatInboxWidget from '../../components/community/ChatInboxWidget';
import useInvitesCounts from '../../hooks/useInvitesCounts';
import AdBanner from '../../components/AdBanner';
import useIsMobile from '../../utils/viewport';
import { PLAYER_ACTIVE_TEAM_KEY } from '../../services/api';

const { Header, Content, Sider } = Layout;

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const SIDER_BG = '#0f1f1a'; // verde muito escuro (quase preto)

const SIDE_GRID_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><path d='M40 0H0v40' fill='none' stroke='rgba(255,255,255,0.05)' stroke-width='1'/></svg>\")";

// Auth check SÍNCRONO — roda antes do primeiro render pra evitar flash
// de painel + redirect lento com window.location.href.
function resolvePlayerAuth() {
  const token = localStorage.getItem('community_token');
  const userData = localStorage.getItem('community_user');
  if (!token || !userData) return { ok: false, user: null };
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    if (payload.exp < now || payload.type !== 'player') {
      localStorage.removeItem('community_token');
      localStorage.removeItem('community_user');
      return { ok: false, user: null };
    }
    return { ok: true, user: JSON.parse(userData) };
  } catch {
    localStorage.removeItem('community_token');
    localStorage.removeItem('community_user');
    return { ok: false, user: null };
  }
}

const CommunityLayout = ({ children, userType = 'player' }) => {
  const isMobile = useIsMobile(768);
  const [collapsed, setCollapsed] = useState(true);
  const [selectedKey, setSelectedKey] = useState('');
  const { pending: invitesPending } = useInvitesCounts('player');

  // Resolve auth ANTES do primeiro render — se inválido, aborta.
  const [authState] = useState(() => {
    if (userType !== 'player') {
      const raw = localStorage.getItem('user');
      return { ok: true, user: raw ? JSON.parse(raw) : null };
    }
    return resolvePlayerAuth();
  });
  const user = authState.user;

  // Sider aberto por padrao no desktop; mobile sempre comeca colapsado.
  useEffect(() => { setCollapsed(isMobile); }, [isMobile]);

  useEffect(() => {
    const path = window.location.pathname;
    if (userType === 'player') {
      if (path.includes('/player/dashboard')) setSelectedKey('dashboard');
      else if (path.includes('/player/feed')) setSelectedKey('feed');
      else if (path.includes('/player/map')) setSelectedKey('community');
      else if (path.includes('/player/procurar')) setSelectedKey('procurar');
      else if (path.includes('/player/invites')) setSelectedKey('invites');
      else if (path.includes('/player/profile')) setSelectedKey('profile');
      else if (path.includes('/player/ratings')) setSelectedKey('ratings');
      else if (path.includes('/player/conquistas')) setSelectedKey('conquistas');
    } else {
      if (path.includes('/community/team')) setSelectedKey('community');
      else if (path.includes('/community/map')) setSelectedKey('map');
      else setSelectedKey('admin');
    }
  }, [userType]);

  const handleLogout = () => {
    if (userType === 'player') {
      localStorage.removeItem('community_token');
      localStorage.removeItem('community_user');
      localStorage.removeItem(PLAYER_ACTIVE_TEAM_KEY);
      window.location.href = '/player/login';
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const playerMenu = [
    { key: 'dashboard', icon: <UserOutlined />, label: 'Dashboard', onClick: () => window.location.href = '/player/dashboard' },
    { key: 'feed',      icon: <FireOutlined />, label: 'Feed', onClick: () => window.location.href = '/player/feed' },
    { key: 'procurar',  icon: <SearchOutlined />, label: 'Procurar', onClick: () => window.location.href = '/player/procurar' },
    { key: 'community', icon: <EnvironmentOutlined />, label: 'Comunidade', onClick: () => window.location.href = '/player/map' },
    {
      key: 'invites',
      icon: <MailOutlined />,
      label: (
        <span>
          Convites {invitesPending > 0 && <Badge count={invitesPending} size="small" style={{ marginLeft: 6 }} />}
        </span>
      ),
      onClick: () => window.location.href = '/player/invites',
    },
    { key: 'ratings',    icon: <StarOutlined />,     label: 'Avaliações', onClick: () => window.location.href = '/player/ratings' },
    { key: 'conquistas', icon: <TrophyOutlined />,   label: 'Conquistas', onClick: () => window.location.href = '/player/conquistas' },
    { key: 'profile',    icon: <SettingOutlined />,  label: 'Meu Perfil', onClick: () => window.location.href = '/player/profile' },
  ];
  const teamMenu = [
    { key: 'community', icon: <TeamOutlined />,        label: 'Comunidade', onClick: () => window.location.href = '/community/team' },
    { key: 'map',       icon: <EnvironmentOutlined />, label: 'Mapa',        onClick: () => window.location.href = '/community/map' },
    { key: 'referrals', icon: <StarOutlined />,        label: 'Indicações',  onClick: () => window.location.href = '/referrals' },
    { key: 'financeiro', icon: <DollarOutlined />,     label: 'Financeiro',  onClick: () => window.location.href = '/comunidade/financeiro' },
    { key: 'admin',     icon: <SettingOutlined />,     label: 'Administração', onClick: () => window.location.href = '/dashboard' },
  ];

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: userType === 'player' ? 'Meu Perfil' : 'Configurações',
        onClick: () => { if (userType === 'player') window.location.href = '/player/profile'; } },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Sair', danger: true, onClick: handleLogout },
    ],
  };

  const brandBadge = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '20px 20px 16px 20px', color: '#fff',
    }}>
      <img src="/pwa/icon-192.png" alt="Futezap" width={40} height={40}
        style={{ borderRadius: 12, boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }} />
      <div>
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.3 }}>Futezap</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5 }}>COMUNIDADE</div>
      </div>
    </div>
  );

  const menuItems = userType === 'player' ? playerMenu : teamMenu;

  // Guard sincrono: sem auth valida, redireciona instantaneamente pro login.
  // <Navigate> nao remonta o app (diferente de window.location.href) — evita
  // flash do dashboard e o "delay de segundos" ate a tela de login aparecer.
  if (userType === 'player' && !authState.ok) {
    return <Navigate to="/player/login" replace />;
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Overlay mobile pra fechar clicando fora */}
      {isMobile && !collapsed && (
        <div
          onClick={() => setCollapsed(true)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000 }}
        />
      )}

      <Sider
        collapsible={false}
        collapsed={isMobile ? collapsed : false}
        width={230}
        collapsedWidth={0}
        style={{
          position: isMobile ? 'fixed' : 'relative',
          zIndex: isMobile ? 10001 : 'auto',
          height: isMobile ? '100vh' : 'auto',
          left: isMobile ? (collapsed ? -240 : 0) : 0,
          transition: 'left 0.3s ease',
          background: SIDER_BG,
          backgroundImage: SIDE_GRID_BG,
          boxShadow: isMobile && !collapsed ? '2px 0 20px rgba(0,0,0,0.35)' : 'none',
          overflow: 'hidden',
        }}
      >
        {brandBadge}
        {/* Switcher jogador ↔ dono no topo do menu lateral, so em mobile.
            Em desktop ele vive no header. */}
        {isMobile && userType === 'player' && (
          <div style={{ padding: '0 16px 12px 16px' }}>
            <ContextSwitcher mode="player" />
          </div>
        )}
        {isMobile && userType === 'team-owner' && (
          <div style={{ padding: '0 16px 12px 16px' }}>
            <ContextSwitcher mode="team-owner" />
          </div>
        )}
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[selectedKey]}
          items={menuItems.map(i => ({ ...i, style: { fontWeight: 500 } }))}
          style={{ background: 'transparent', borderRight: 'none', fontSize: 14 }}
        />
        <div style={{
          position: 'absolute', bottom: 20, left: 20, right: 20,
          padding: 12, borderRadius: 12,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.65)', fontSize: 11,
        }}>
          Feito no Brasil 🇧🇷<br />pra quem joga bola.
        </div>
      </Sider>

      <Layout style={{ background: 'transparent' }}>
        <Header style={{
          padding: isMobile ? '0 8px' : '0 24px',
          background: '#fff',
          borderBottom: '1px solid #eef1f4',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 64,
          // Impede overflow horizontal quando header tem muitos itens em mobile
          overflowX: 'hidden', minWidth: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 18 }} />}
                onClick={() => setCollapsed(!collapsed)}
              />
            )}
            {userType === 'player' && !window.location.pathname.startsWith('/time/') && (
              <>
                {/* ContextSwitcher escondido em mobile — cabe no menu lateral */}
                {!isMobile && <ContextSwitcher mode="player" />}
                <TeamSwitcher isMobile={isMobile} />
              </>
            )}
          </div>

          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 12, flexShrink: 0 }}>
              {userType === 'player' && (
                <PushNotificationButton
                  compact={isMobile}
                  basePath="/push/player"
                  contextText="Este dispositivo vai receber notificações quando o organizador do seu time abrir novo jogo, quando você entrar da lista de espera pro time principal, e outras atualizações importantes."
                />
              )}
              <ChatInboxWidget />
              <NotificationCenter userType={userType} />
              <Dropdown menu={userMenu} placement={isMobile ? 'bottomLeft' : 'bottomRight'} trigger={['click']}>
                <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', height: 'auto' }}>
                  <Avatar
                    size={36}
                    src={user.profile_photo}
                    icon={<UserOutlined />}
                    style={{
                      background: user.profile_photo ? 'transparent' : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                    }}
                  />
                  {!isMobile && (
                    <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{user.name?.split(' ')[0] || 'Jogador'}</div>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>{userType === 'player' ? 'Jogador' : 'Organizador'}</div>
                    </div>
                  )}
                </Button>
              </Dropdown>
            </div>
          )}
        </Header>

        <Content style={{ background: 'transparent', minHeight: 'calc(100vh - 64px)' }}>
          {userType === 'player' && <AdBanner communityMode />}
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default CommunityLayout;
